import { addBusinessDays } from "@/lib/onboarding/plan-dates";

// Pure description of the action-required notification kinds (T-196): safe for client code.
// A notification is action-required when it has an `actionKey` of the form "<kind>:<subjectId>".

export type ReminderCfg = {
  /** Business days after the notification before the first reminder. */
  firstDays: number;
  /** Business days between later reminders. */
  everyDays: number;
  /** Reminders per item; 0 = this kind reminds on its own schedule. */
  max: number;
  /** Calendar days still open before backups hear about it; null = never. */
  escalateDays: number | null;
};

const R = (firstDays: number, everyDays: number, max: number, escalateDays: number | null): ReminderCfg => ({ firstDays, everyDays, max, escalateDays });

export const ACTION_KINDS = {
  access_request: { label: "Access requests", escalateTo: "users:manage", reminder: R(1, 1, 3, 3) },
  access_more_time: { label: "Guest more-time requests", escalateTo: "users:manage", reminder: R(1, 1, 3, 3) },
  deletion_request: { label: "Deletion requests", escalateTo: "deletion_requests:review", reminder: R(1, 2, 3, 4) },
  user_approval: { label: "New accounts to approve", escalateTo: "users:manage", reminder: R(1, 1, 3, 3) },
  project_approval: { label: "Projects to approve", escalateTo: "projects:approve", reminder: R(1, 1, 3, 3) },
  project_request: { label: "Projects requested", escalateTo: "projects:approve", reminder: R(1, 2, 3, null) },
  project_package_close: { label: "Bexio packages to close", escalateTo: "projects:approve", reminder: R(3, 3, 2, null) },
  project_over_budget: { label: "Projects over budget", escalateTo: "projects:approve", reminder: R(1, 2, 3, null) },
  training_review: { label: "Training work to review", escalateTo: "onboarding:own", reminder: R(1, 1, 5, 3) },
  training_sent_back: { label: "Training items sent back", escalateTo: undefined, reminder: R(2, 2, 3, null) },
  runbook_review: { label: "Runbook reviews", escalateTo: undefined, reminder: R(5, 5, 3, null) },
  article_stale: { label: "Stale articles", escalateTo: undefined, reminder: R(5, 5, 3, null) },
  rca: { label: "RCAs owed", escalateTo: undefined, reminder: R(0, 0, 0, null) },
} as const satisfies Record<string, { label: string; escalateTo: string | undefined; reminder: ReminderCfg }>;

export type ActionKind = keyof typeof ACTION_KINDS;
export const ACTION_KIND_LIST = Object.keys(ACTION_KINDS) as ActionKind[];
/** Admin overrides: JSON `{ [kind]: Partial<ReminderCfg> }` in this setting. */
export const REMINDER_SETTING_KEY = "notifications.action_reminders";

export const actionKey = (kind: ActionKind, id: number) => `${kind}:${id}`;

export function parseActionKey(key: string | null | undefined): { kind: ActionKind; id: number } | null {
  const [kind, raw] = (key ?? "").split(":");
  const id = Number(raw);
  return kind in ACTION_KINDS && Number.isInteger(id) && id > 0 ? { kind: kind as ActionKind, id } : null;
}

export type ReminderOverrides = Partial<Record<ActionKind, Partial<ReminderCfg>>>;

export function parseOverrides(raw: string | null | undefined): ReminderOverrides {
  try {
    const v: unknown = JSON.parse(raw ?? "{}");
    return v && typeof v === "object" && !Array.isArray(v) ? (v as ReminderOverrides) : {};
  } catch {
    return {};
  }
}

export function reminderFor(kind: ActionKind, overrides: ReminderOverrides): ReminderCfg {
  return { ...ACTION_KINDS[kind].reminder, ...overrides[kind] };
}

type Timing = { createdAt: Date; lastRemindedAt: Date | null; remindCount: number; snoozedUntil: Date | null };

const isWeekend = (d: Date) => d.getUTCDay() === 0 || d.getUTCDay() === 6;

/** True when the next reminder of an open item is due now. Never on weekends, never while snoozed. */
export function reminderDue(t: Timing, cfg: ReminderCfg, now: Date): boolean {
  if (cfg.max <= 0 || t.remindCount >= cfg.max || isWeekend(now)) return false;
  if (t.snoozedUntil && t.snoozedUntil > now) return false;
  const due = t.lastRemindedAt ? addBusinessDays(t.lastRemindedAt, cfg.everyDays) : addBusinessDays(t.createdAt, cfg.firstDays);
  return due <= now;
}

export const escalationDue = (t: { createdAt: Date; escalatedAt: Date | null }, cfg: ReminderCfg, now: Date) =>
  cfg.escalateDays !== null && t.escalatedAt === null && now.getTime() - t.createdAt.getTime() >= cfg.escalateDays * 86_400_000;
